import { useEffect, useMemo, useState } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import {
  Alert,
  BotonExportarExcel,
  Card,
  DesktopOnly,
  Group,
  ListaTarjetasSkeleton,
  MobileOnly,
  PaginadorTabla,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { AdminAuditEntry } from "../services/auditoria-service";
import { listarAuditoriaCambios } from "../services/auditoria-service";
import { categoriasParaScope, type AuditoriaScope } from "../data/categorias";
import { useAuditoriaCambios } from "../hooks/use-auditoria";
import { accionesPresentes } from "../utils/filtrar-auditoria";
import { CambioRow } from "./CambioRow";
import { CambioMobileCard } from "./CambioMobileCard";
import { CambiosFiltrosCard } from "./CambiosFiltrosCard";

const PAGE_SIZE = 15;

/**
 * Una sección por scope (operaciones/admin/accesos). TODOS los filtros
 * (categoría, acción, texto con debounce) y el paginado van al SERVIDOR
 * (endpoint /auditoria-cambios/paginado); el scope viaja como CSV de
 * categorías cuando no hay una categoría puntual elegida.
 */
export function SeccionCambios({ scope }: { scope: AuditoriaScope }) {
  const categoriasScope = categoriasParaScope(scope);

  const [categoria, setCategoria] = useState("");
  const [accion, setAccion] = useState("");
  const [texto, setTexto] = useState("");
  const [page, setPage] = useState(1);
  const [textoDebounced] = useDebouncedValue(texto, 350);

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [categoria, accion, textoDebounced]);

  const filtroCategoriaBackend =
    categoriasScope.length === 1
      ? categoriasScope[0]
      : categoria && categoriasScope.includes(categoria)
      ? categoria
      : undefined;

  const filtrosBase = useMemo(
    () => ({
      ...(filtroCategoriaBackend
        ? { categoria: filtroCategoriaBackend }
        : { categorias: categoriasScope.join(",") }),
      ...(accion ? { accion } : {}),
      ...(textoDebounced.trim() ? { texto: textoDebounced.trim() } : {}),
    }),
    [filtroCategoriaBackend, categoriasScope, accion, textoDebounced],
  );

  const cambios = useAuditoriaCambios({ ...filtrosBase, page, pageSize: PAGE_SIZE });
  const items = cambios.data?.items ?? [];
  const total = cambios.data?.total ?? 0;
  const desde = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const hasta = Math.min(page * PAGE_SIZE, total);

  // Opciones del dropdown de acción · derivadas de la página visible (+ la
  // seleccionada, para que no desaparezca del Select al filtrar).
  const opcionesAccion = useMemo(
    () =>
      Array.from(new Set([...accionesPresentes(items), ...(accion ? [accion] : [])])).sort(),
    [items, accion],
  );

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodos = async (): Promise<AdminAuditEntry[]> => {
    const todos: AdminAuditEntry[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarAuditoriaCambios({ ...filtrosBase, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  if (cambios.isError) {
    return (
      <Alert variant="error" icon={<IconAlertTriangle size={16} />}>
        Error: {(cambios.error as Error).message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <CambiosFiltrosCard
        categoriasScope={categoriasScope}
        categoria={categoria}
        onCategoriaChange={setCategoria}
        accion={accion}
        onAccionChange={setAccion}
        accionesDisponibles={opcionesAccion}
        texto={texto}
        onTextoChange={setTexto}
        total={cambios.data?.total}
        desde={desde}
        hasta={hasta}
        filtroCategoriaBackend={filtroCategoriaBackend}
      />

      <PaginadorTabla cargando={cambios.isPending} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
      <DesktopOnly>
        <Card padding="lg">
          <Group justify="flex-end" className="mb-2">
            <BotonExportarExcel
              archivo={`auditoria-${scope}`}
              hoja="Auditoría"
              obtenerFilas={obtenerTodos}
              columnas={[
                { header: "Cuándo", value: (e) => e.ocurridoAt },
                { header: "Categoría", value: (e) => e.categoria },
                { header: "Acción", value: (e) => e.accion },
                { header: "Entidad", value: (e) => e.entidadDescripcion },
                { header: "Usuario", value: (e) => e.usuarioEmail },
                { header: "Detalles", value: (e) => e.detallesJson },
              ]}
            />
          </Group>
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm">
              <Thead>
                <Tr>
                  <Th>Cuándo</Th>
                  <Th>Categoría</Th>
                  <Th>Acción</Th>
                  <Th>Entidad</Th>
                  <Th>Usuario</Th>
                  <Th>Detalles</Th>
                </Tr>
              </Thead>
              {cambios.isPending ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, {}, {}, { lineas: 2 }, {}, {}]} filas={PAGE_SIZE} />
              ) : (
                <Tbody>
                  {items.map((e) => (
                    <CambioRow key={e.id} entry={e} />
                  ))}
                </Tbody>
              )}
            </Table>
          </div>
        </Card>
      </DesktopOnly>

      <MobileOnly>
        {cambios.isPending ? (
          <ListaTarjetasSkeleton />
        ) : (
          <Stack gap="xs">
            {items.map((e) => (
              <CambioMobileCard key={e.id} entry={e} />
            ))}
          </Stack>
        )}
      </MobileOnly>

      <PaginadorTabla cargando={cambios.isPending} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="eventos" />
    </Stack>
  );
}
